/**
 * Microphone Pre-Check Utility
 * Validates microphone availability and permissions before starting a session
 */

export interface MicrophoneCheckResult {
  success: boolean;
  error?: string;
  errorType?:
    | "NO_DEVICE"
    | "PERMISSION_DENIED"
    | "SYSTEM_MUTED"
    | "BROWSER_NOT_SUPPORTED"
    | "UNKNOWN";
}

/**
 * Checks if the browser supports the MediaDevices API
 */
const isBrowserSupported = (): boolean => {
  return !!(
    navigator.mediaDevices &&
    typeof navigator.mediaDevices.getUserMedia === "function" &&
    typeof navigator.mediaDevices.enumerateDevices === "function"
  );
};

/**
 * Checks if any audio input devices are available
 */
const checkMicrophoneDevice = async (): Promise<boolean> => {
  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const audioInputDevices = devices.filter(
      (device) => device.kind === "audioinput"
    );
    return audioInputDevices.length > 0;
  } catch (error) {
    console.error("Error enumerating devices:", error);
    return false;
  }
};

/**
 * Requests microphone access and checks if it's working
 */
const requestMicrophoneAccess = async (): Promise<MediaStream | null> => {
  try {
    const stream = await navigator.mediaDevices.getUserMedia({
      audio: true,
    });
    return stream;
  } catch (error) {
    console.error("Error requesting microphone access:", error);
    return null;
  }
};

/**
 * Checks if the microphone is muted at system level by analyzing audio levels
 * Currently unused but kept for future implementation
 */
const _checkMicrophoneMuted = async (stream: MediaStream): Promise<boolean> => {
  return new Promise((resolve) => {
    try {
      const audioContext = new AudioContext();
      const analyser = audioContext.createAnalyser();
      const microphone = audioContext.createMediaStreamSource(stream);
      const dataArray = new Uint8Array(analyser.frequencyBinCount);

      microphone.connect(analyser);
      analyser.fftSize = 256;

      let checkCount = 0;
      const maxChecks = 10; // Check for 1 second (10 * 100ms)
      let hasAudioActivity = false;

      const checkAudioLevel = () => {
        analyser.getByteFrequencyData(dataArray);
        const average =
          dataArray.reduce((sum, value) => sum + value, 0) / dataArray.length;

        // If we detect any audio activity, it's not muted
        if (average > 0) {
          hasAudioActivity = true;
        }

        checkCount++;

        if (checkCount >= maxChecks) {
          // Clean up
          microphone.disconnect();
          audioContext.close();

          // If no audio activity detected after checks, likely muted
          // Note: This is not 100% reliable as user might just be silent
          resolve(!hasAudioActivity);
        } else {
          setTimeout(checkAudioLevel, 100);
        }
      };

      checkAudioLevel();
    } catch (error) {
      console.error("Error checking microphone mute status:", error);
      // If we can't check, assume it's not muted to avoid false positives
      resolve(false);
    }
  });
};

/**
 * Performs a comprehensive microphone pre-check
 * Returns a result object with success status and error details if any
 */
export const performMicrophoneCheck =
  async (): Promise<MicrophoneCheckResult> => {
    // 1. Check if browser supports MediaDevices API
    if (!isBrowserSupported()) {
      return {
        success: false,
        error:
          "Your browser does not support microphone access. Please use a modern browser like Chrome, Firefox, or Edge.",
        errorType: "BROWSER_NOT_SUPPORTED",
      };
    }

    // 2. Check if microphone device is detected
    const hasDevice = await checkMicrophoneDevice();
    if (!hasDevice) {
      return {
        success: false,
        error:
          "No microphone detected. Please connect a microphone and try again.",
        errorType: "NO_DEVICE",
      };
    }

    // 3. Request microphone access (checks browser permissions)
    const stream = await requestMicrophoneAccess();
    if (!stream) {
      return {
        success: false,
        error:
          "Microphone access denied. Please allow microphone access in your browser settings and try again.",
        errorType: "PERMISSION_DENIED",
      };
    }

    // 4. Check if microphone is muted at system level
    // Note: This check is optional and may not be 100% reliable
    // Commenting out for now as it requires user to make sound
    // const isMuted = await checkMicrophoneMuted(stream);
    // if (isMuted) {
    //   // Clean up stream
    //   stream.getTracks().forEach((track) => track.stop());
    //   return {
    //     success: false,
    //     error: 'Microphone appears to be muted at system level. Please unmute your microphone and try again.',
    //     errorType: 'SYSTEM_MUTED',
    //   };
    // }

    // Clean up stream - we'll request it again when actually starting the session
    stream.getTracks().forEach((track) => track.stop());

    return {
      success: true,
    };
  };

/**
 * Stops all tracks in a media stream
 */
export const stopMediaStream = (stream: MediaStream | null): void => {
  if (stream) {
    stream.getTracks().forEach((track) => track.stop());
  }
};
